import type { FemaCredentialState, FemaEnvironmentClass } from 'types/femaTenant.types';

/**
 * FE-11: the texts, thresholds and variants of the FEMA credential page (MULTICOG design read
 * §3.3-§3.6, contract §3 and §4). The page reads these constants; no component writes a text of
 * its own.
 */

export const FEMA_CREDENTIALS_TITLE = 'FEMA Credentials';

/** The Bootstrap variants this page uses. */
export type FemaCredentialVariant = 'secondary' | 'info' | 'success' | 'warning' | 'danger';

/**
 * Contract §3: the expiry warning days. RescAlert internal operational policy (Req §28); not a
 * FEMA schedule. The backend constant `'expiry-warning-days'` has the same values.
 */
export const FEMA_EXPIRY_WARNING_DAYS = [90, 60, 30, 14, 7] as const;

/** Design read §3.6: 90 and 60 = info; 30 = warning; 14, 7 and expired = danger. */
export const FEMA_EXPIRY_BAND_VARIANTS = {
  '90': 'info',
  '60': 'info',
  '30': 'warning',
  '14': 'danger',
  '7': 'danger',
  expired: 'danger',
} as const satisfies Record<string, FemaCredentialVariant>;

/** Design read §3.6: the sentence that follows every expiry warning. */
export const FEMA_EXPIRY_POLICY_SENTENCE =
  'This schedule is RescAlert internal operational policy, not a FEMA requirement.';

export const FEMA_EXPIRY_RENEW_SENTENCE =
  'Contact FEMA to renew it. Then upload the new certificate here.';

/** The actions a status card can offer (q10 default: the client uploads). */
export type FemaCredentialAction =
  | 'upload'
  | 'replace'
  | 'validate'
  | 'enable'
  | 'validateReplacement'
  | 'rotate';

export const FEMA_CREDENTIAL_ACTION_LABELS: Record<FemaCredentialAction, string> = {
  upload: 'Upload a credential',
  replace: 'Replace the credential',
  validate: 'Validate',
  enable: 'Enable',
  validateReplacement: 'Validate the replacement',
  rotate: 'Swap in the replacement',
};

/**
 * Contract §4 state → card variant and actions (design read §3.3 table, with `none`, `stored`,
 * `validated` and `retired` added). `expiring` starts at `warning`; the 14 and 7 day bands raise
 * it to `danger` (see `credentialState.utils.ts`).
 */
export const FEMA_CREDENTIAL_STATE_PRESENTATION: Record<
  FemaCredentialState,
  { variant: FemaCredentialVariant; actions: readonly FemaCredentialAction[] }
> = {
  none: { variant: 'secondary', actions: ['upload'] },
  stored: { variant: 'warning', actions: ['validate', 'replace'] },
  validated: { variant: 'info', actions: ['enable', 'replace'] },
  active: { variant: 'success', actions: ['replace'] },
  expiring: { variant: 'warning', actions: ['replace'] },
  expired: { variant: 'danger', actions: ['replace'] },
  failed: { variant: 'danger', actions: ['replace'] },
  retired: { variant: 'secondary', actions: [] },
};

/** The text of the `none` state: what FEMA must issue first. */
export const FEMA_CREDENTIAL_NONE_DETAIL =
  'FEMA must issue a COG certificate for this class first. Then upload it here.';

/** The class badge variant. LIVE PRODUCTION is always `danger`. */
export const FEMA_CLASS_BADGE_VARIANTS: Record<FemaEnvironmentClass, FemaCredentialVariant> = {
  tdl: 'secondary',
  demo: 'info',
  production: 'danger',
};

/** Design read §3.5: the rotation sentence on the Replace flow. */
export const FEMA_ROTATION_SENTENCE =
  'Your current certificate stays active until the new one passes validation. Sending does not stop.';

/** Design read §3.4: the stepper steps. */
export const FEMA_UPLOAD_STEPS = ['File', 'Passwords', 'Review', 'Validate', 'Enabled'] as const;
export type FemaUploadStep = (typeof FEMA_UPLOAD_STEPS)[number];

export const FEMA_PASSWORD_LABELS = {
  keystorePassword: 'Keystore password',
  keystorePasswordHelp: 'This password opens the file.',
  privateKeyPassword: 'Private-key password',
  privateKeyPasswordHelp: 'This password protects the key inside the file.',
  reenterHelp:
    'For your security, the page does not keep the passwords. Enter them again to store the credential.',
} as const;

export const FEMA_REVIEW_CONFIRM_LABEL = "This is my organization's certificate.";

/** The text when the value is not recorded. */
export const FEMA_NOT_RECORDED = 'Not recorded';
